import { DOCUMENT, inject, Injectable } from '@angular/core';
import { LoggingService } from '@athena/util-monitoring';
import { STORAGE_APP_PREFIX } from '../constants/storage.constant';

/**
 * Service providing a safe, namespaced wrapper around browser localStorage.
 *
 * Features:
 * - Automatic key prefixing to avoid collisions
 * - JSON serialization/deserialization
 * - Backwards compatibility with unprefixed keys
 * - Centralised error handling and logging
 */
@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  readonly #loggingService = inject(LoggingService);
  readonly #document = inject(DOCUMENT);

  /**
   * Builds the fully-qualified storage key using the application prefix.
   *
   * @param key Raw storage key
   * @returns Prefixed storage key
   */
  private getKey(key: string): string {
    return `${STORAGE_APP_PREFIX}${key}`;
  }

  /**
   * Saves a value to localStorage.
   *
   * - Strings are stored as-is
   * - Non-string values are serialized to JSON
   *
   * @typeParam T Type of the value being stored
   * @param key The storage key (without prefix)
   * @param value The value to store
   */
  save<T>(key: string, value: T): void {
    try {
      const prefixedKey = this.getKey(key);
      const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
      this.#document.defaultView?.localStorage.setItem(prefixedKey, serializedValue);
    } catch (error) {
      this.#logStorageFailure(`Error saving to localStorage for key "${key}":`, error);
    }
  }

  /**
   * Retrieves a value from localStorage.
   *
   * Resolution order:
   * 1. Prefixed key
   * 2. Unprefixed key (backwards compatibility)
   *
   * If the stored value is valid JSON, it will be parsed automatically.
   * Otherwise, the raw string value is returned.
   *
   * @typeParam T Expected return type
   * @param key The storage key (without prefix)
   * @returns The stored value or `null` if not found or on error
   */
  get<T>(key: string): T | null {
    try {
      const prefixedKey = this.getKey(key);
      const localStorage = this.#document.defaultView?.localStorage;
      if (!localStorage) return null;

      // Try prefixed key first
      let value = localStorage.getItem(prefixedKey);

      // Fallback to unprefixed key for backwards compatibility
      if (value === null) {
        value = localStorage.getItem(key);
      }

      if (value === null) {
        return null;
      }

      // Attempt JSON parsing; fall back to raw string
      try {
        return JSON.parse(value) as T;
      } catch {
        return value as unknown as T;
      }
    } catch (error) {
      this.#logStorageFailure(`Error retrieving from localStorage for key "${key}":`, error);
      return null;
    }
  }

  /**
   * Updates an existing value in localStorage.
   *
   * The method checks for the existence of either:
   * - the prefixed key, or
   * - the unprefixed key (backwards compatibility)
   *
   * If found, the value is saved using the prefixed key.
   *
   * @typeParam T Type of the value being stored
   * @param key The storage key (without prefix)
   * @param value The new value to store
   * @returns `true` if the key existed and was updated, otherwise `false`
   */
  update<T>(key: string, value: T): boolean {
    try {
      const prefixedKey = this.getKey(key);
      const localStorage = this.#document.defaultView?.localStorage;
      if (!localStorage) return false;

      const existingKey = localStorage.getItem(prefixedKey) !== null ? prefixedKey : localStorage.getItem(key) !== null ? key : null;

      if (existingKey) {
        this.save(key, value);
        return true;
      }

      return false;
    } catch (error) {
      this.#logStorageFailure(`Error updating localStorage for key "${key}":`, error);
      return false;
    }
  }

  /**
   * Removes a value from localStorage.
   *
   * Both prefixed and unprefixed keys are removed to ensure cleanup
   * during migrations.
   *
   * @param key The storage key (without prefix)
   */
  remove(key: string): void {
    try {
      const prefixedKey = this.getKey(key);
      const localStorage = this.#document.defaultView?.localStorage;
      if (!localStorage) return;

      localStorage.removeItem(prefixedKey);
      localStorage.removeItem(key);
    } catch (error) {
      this.#logStorageFailure(`Error removing from localStorage for key "${key}":`, error);
    }
  }

  /**
   * Removes multiple keys from localStorage.
   *
   * @param keys Array of storage keys (without prefix)
   */
  removeAll(keys: string[]): void {
    keys.forEach((key) => this.remove(key));
  }

  /**
   * Clears all application-owned localStorage entries.
   *
   * Only keys starting with `STORAGE_APP_PREFIX` are removed.
   * This prevents accidental deletion of data belonging to
   * other applications or libraries.
   */
  clear(): void {
    try {
      const localStorage = this.#document.defaultView?.localStorage;
      if (!localStorage) return;

      Object.keys(localStorage)
        .filter((key) => key.startsWith(STORAGE_APP_PREFIX))
        .forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      this.#logStorageFailure('Error clearing prefixed localStorage:', error);
    }
  }

  // SecurityError (iOS Safari with "Block All Cookies", Private Browsing, or
  // Lockdown Mode) and QuotaExceededError (older-iOS private mode) are user
  // privacy choices, not product failures - route them to warn so they stay
  // in the console but don't reach Sentry. Everything else (SyntaxError from
  // JSON.parse, unexpected TypeError, etc.) still goes through error.
  #logStorageFailure(message: string, error: unknown): void {
    const name = error instanceof Error ? error.name : '';
    const isUserDenial = name === 'SecurityError' || name === 'QuotaExceededError';
    if (isUserDenial) {
      this.#loggingService.warn(message, error);
    } else {
      this.#loggingService.error(message, error);
    }
  }
}
